import { Link } from "react-router-dom";
import type { Wallet, WalletStatus, WalletType } from "@/types";
import { Badge } from "@/components/ui/badge";

const TYPE_META: Record<WalletType, { label: string; bg: string; text: string }> = {
  hot: { label: "HOT", bg: "var(--ads-danger-tint)", text: "var(--ads-danger)" },
  cold: { label: "CLD", bg: "var(--ads-info-soft)", text: "var(--ads-info)" },
  warm: { label: "WRM", bg: "var(--ads-warning-soft)", text: "var(--ads-warning)" },
};

const STATUS_META: Record<WalletStatus, { label: string; variant: "success" | "danger" | "pending" | "neutral" }> = {
  active: { label: "Active", variant: "success" },
  frozen: { label: "Frozen", variant: "danger" },
  pending: { label: "Pending", variant: "pending" },
  sandbox: { label: "Sandbox", variant: "neutral" },
};

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export function WalletRow({ wallet, href }: { wallet: Wallet; href?: string }) {
  const type = TYPE_META[wallet.type];
  const status = STATUS_META[wallet.status];
  const mono = "font-mono tabular-nums text-[13px]";

  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-bg">
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          {href ? (
            <Link to={href} className="text-sm font-semibold text-ink transition-colors hover:text-primary">
              {wallet.name}
            </Link>
          ) : (
            <div className="text-sm font-semibold text-ink">{wallet.name}</div>
          )}
        </div>
      </td>
      <td className="px-4 py-3.5 text-sm text-text">{wallet.chain}</td>
      <td className="px-4 py-3.5 text-sm text-text">{wallet.provider}</td>
      <td className="px-4 py-3.5">
        <span
          className="inline-flex items-center px-2 py-[3px] rounded-[4px] text-[11px] font-bold uppercase tracking-[0.04em]"
          style={{ background: type.bg, color: type.text }}
        >
          {type.label}
        </span>
      </td>
      <td className="px-4 py-3.5">
        <div className={wallet.status === "pending" ? "text-muted" : mono}>
          {wallet.status === "pending" ? "—" : wallet.status === "sandbox" ? "$0 (testnet)" : usd.format(wallet.balanceUsd!)}
        </div>
      </td>
      <td className="px-4 py-3.5">
        {wallet.lastSyncAgo ? (
          <span className="inline-flex items-center gap-1 text-sm text-muted">
            <span
              className="size-1.5 rounded-full shrink-0"
              style={{ background: wallet.status === "frozen" ? "var(--ads-warning)" : "var(--ads-success)" }}
            />
            {wallet.lastSyncAgo} ago
          </span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3.5">
        <Badge variant={status.variant} dot={false}>{status.label}</Badge>
      </td>
      <td className="px-4 py-3.5">
        <button
          type="button"
          aria-label={`Open actions for ${wallet.name}`}
          className="grid size-7 place-items-center rounded-md text-muted transition-colors hover:bg-line hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
        >
          <span aria-hidden className="text-lg leading-none">⋯</span>
        </button>
      </td>
    </tr>
  );
}